import React, {useContext, useState, useRef, useEffect} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {
    DashboardOutlined,
    DatabaseOutlined,
    ThunderboltOutlined,
    TeamOutlined,
    LogoutOutlined,
    MenuOutlined,
    CloseOutlined,
    DownOutlined,
    IdcardOutlined,
    CalendarOutlined,
    BarChartOutlined
} from "@ant-design/icons";
import {AuthContext} from "../../context/AuthContext";
import {getDisplayName, getInitials} from "../../utils/user";
import logo from "../../Assets/logo23.png";
import "./Header.css";
import SubscriptionBell from "./SubscriptionBell";

const todayLabel = () =>
    new Date().toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
    });

const Header = () => {
    const {logout, user} = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const isSuperAdmin = user?.userRole === "superadmin";

    const [mobileOpen, setMobileOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const profileRef = useRef(null);

    const navItems = isSuperAdmin
        ? [
            { path: "/", icon: <DashboardOutlined />, label: "Dashboard" },
            { path: "/students", icon: <DatabaseOutlined />, label: "Students" },
            { path: "/customers", icon: <TeamOutlined />, label: "Customers" },
            { path: "/report", icon: <BarChartOutlined />, label: "Report" },
        ]
        : [
            { path: "/", icon: <DashboardOutlined />, label: "Dashboard" },
            { path: "/students", icon: <DatabaseOutlined />, label: "Students" },
            { path: "/race-management", icon: <ThunderboltOutlined />, label: "Race Management" },
            { path: "/report", icon: <BarChartOutlined />, label: "Report" },
        ];

    const isActive = (path) => location.pathname === path;

    const closeAll = () => {
        setMobileOpen(false);
        setProfileOpen(false);
    };

    const handleLogout = () => {
        logout();
        navigate("/login");
        closeAll();
    };

    // Close the profile dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setProfileOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <header className="app-header">
            <div className="header-inner">
                <Link to="/" className="brand" onClick={closeAll}>
                    <img src={logo} alt="" className="brand-logo"/>
                    <span className="brand-name">Vidyarjana</span>
                </Link>

                <button
                    className="nav-toggle"
                    onClick={() => setMobileOpen((prev) => !prev)}
                    aria-label="Toggle menu"
                    aria-expanded={mobileOpen}
                >
                    {mobileOpen ? <CloseOutlined/> : <MenuOutlined/>}
                </button>

                <nav className={`main-nav ${mobileOpen ? "open" : ""}`}>
                    <ul>
                        {navItems.map((item) => (
                            <li key={item.path}>
                                <Link
                                    to={item.path}
                                    className={isActive(item.path) ? "active" : ""}
                                    onClick={closeAll}
                                >
                                    {item.icon}
                                    <span>{item.label}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <div className="mobile-date">
                        <CalendarOutlined/>
                        <span>{todayLabel()}</span>
                    </div>

                    {/* Mobile-only profile actions — desktop uses the dropdown on the right */}
                    <div className="mobile-profile">
                        <div className="mobile-profile-header">
                            <span className="mobile-profile-avatar">{getInitials(user)}</span>
                            <div className="mobile-profile-meta">
                                <div className="mobile-profile-name">
                                    {getDisplayName(user, "Account")}
                                </div>
                                {user?.email && (
                                    <div className="mobile-profile-email">{user.email}</div>
                                )}
                            </div>
                        </div>

                        <Link to="/profile" className="mobile-profile-link" onClick={closeAll}>
                            <IdcardOutlined />
                            <span>View Profile</span>
                        </Link>
                        <button className="mobile-profile-link logout" onClick={handleLogout}>
                            <LogoutOutlined />
                            <span>Logout</span>
                        </button>
                    </div>
                </nav>

                {!isSuperAdmin && <SubscriptionBell renewDate={user?.renewDate} />}


                <div className="header-date">
                    <CalendarOutlined/>
                    <span>{todayLabel()}</span>
                </div>

                <div className="header-right" ref={profileRef}>
                    <button
                        type="button"
                        className={`profile-trigger ${profileOpen ? "active" : ""}`}
                        onClick={() => setProfileOpen((prev) => !prev)}
                        aria-expanded={profileOpen}
                    >
                       <span className="profile-avatar">
                            {getInitials(user)}
                       </span>
                        <span className="profile-name">{getDisplayName(user, "Account")}</span>
                        <DownOutlined className={`chevron ${profileOpen ? "flip" : ""}`}/>
                    </button>

                    <ul className={`dropdown-menu profile-menu ${profileOpen ? "open" : ""}`}>
                        <li>
                            <Link to="/profile" onClick={closeAll}>
                                <IdcardOutlined/>
                                <span>View Profile</span>
                            </Link>
                        </li>
                        <li>
                            <button type="button" onClick={handleLogout}>
                                <LogoutOutlined/>
                                <span>Logout</span>
                            </button>
                        </li>
                    </ul>
                </div>
            </div>

            {mobileOpen && <div className="nav-overlay" onClick={closeAll} aria-hidden="true"/>}
        </header>
    );
};

export default Header;